import React, { useCallback, useEffect, useRef, useState } from 'react'
import 'css/slide-annotation.css'

/* Drawing over the presented slide.
 *
 * Tool behaviour and colours are the mockup's (AllForOne_Delivery.html):
 * pen 3.5px solid, highlighter 20px at 0.32 alpha, eraser 28px via
 * destination-out, and the five brand colours from its swatch row.
 *
 * Annotations are per slide and kept in memory for the lesson: going back a
 * slide brings your marks back, the way the mockup's slideMarkupData does.
 * They are deliberately NOT persisted or broadcast to students -- the mockup
 * only ever draws on the teacher's board, and marks made over one class's
 * slide should not turn up in the next.
 */

const COLORS = ['#A8543D', '#2C5FA0', '#372B1B', '#3E7D5B', '#B85C82']
const PEN_WIDTH = 3.5
const HIGHLIGHTER_WIDTH = 20
const ERASER_WIDTH = 28
const HIGHLIGHTER_ALPHA = 0.32
/* Undo keeps full-canvas snapshots, which are cheap to get right and not free
   in memory. A teacher wants a few steps back, not a full session's history. */
const MAX_HISTORY = 20

const hexToRgba = (hex, alpha) => {
	const r = parseInt(hex.slice(1, 3), 16)
	const g = parseInt(hex.slice(3, 5), 16)
	const b = parseInt(hex.slice(5, 7), 16)
	return `rgba(${r},${g},${b},${alpha})`
}

const Icon = ({ d, filled = false }) => (
	<svg width="17" height="17" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
		{d}
	</svg>
)

const TOOLS = [
	{ id: 'pen', label: 'Pen', icon: <Icon d={<path d="M12 19l7-7a2.1 2.1 0 0 0-3-3l-7 7-1 4z" />} /> },
	{ id: 'highlighter', label: 'Highlighter', icon: <Icon d={<><path d="M4 20h6" /><path d="M15 4l5 5-8 8H7v-5z" /></>} /> },
	{ id: 'text', label: 'Text', icon: <Icon d={<><path d="M5 6V4h14v2" /><path d="M12 4v16" /><path d="M9 20h6" /></>} /> },
	{ id: 'eraser', label: 'Eraser', icon: <Icon d={<><path d="M4 16l8-8 6 6-6 6H7z" /><path d="M10 20h10" /></>} /> },
]

export const useSlideAnnotation = () => {
	const [open, setOpen] = useState(false)
	return { annotationOpen: open, toggleAnnotation: () => setOpen(v => !v) }
}

/* `highContrast` / `onToggleHighContrast` are optional: the mockup puts the
   High Contrast control inside this panel (its .markup-hc-row), between the
   colours and the actions, rather than on the board toolbar. Passing them is
   what moves it there; omitting them renders the panel exactly as before. */
export const SlideAnnotation = ({ slideKey, active, highContrast, onToggleHighContrast }) => {
	const canvasRef = useRef(null)
	const drawingRef = useRef(false)
	const historyRef = useRef([])
	/* Marks for every slide visited this lesson, keyed by slide, so going back
	   restores what was drawn there. */
	const perSlideRef = useRef({})
	const lastKeyRef = useRef(slideKey)

	const [tool, setTool] = useState('pen')
	const [color, setColor] = useState(COLORS[0])
	const toolRef = useRef(tool)
	const colorRef = useRef(color)
	useEffect(() => { toolRef.current = tool }, [tool])
	useEffect(() => { colorRef.current = color }, [color])

	const ctx = () => canvasRef.current?.getContext('2d')

	const snapshot = useCallback(() => {
		const c = canvasRef.current
		if (!c) return null
		try { return c.toDataURL() } catch { return null }
	}, [])

	const restore = useCallback((dataUrl) => {
		const c = canvasRef.current
		const context = ctx()
		if (!c || !context) return
		context.save()
		context.setTransform(1, 0, 0, 1, 0, 0)
		context.clearRect(0, 0, c.width, c.height)
		context.restore()
		if (!dataUrl) return
		const img = new window.Image()
		img.onload = () => {
			const cc = ctx()
			if (!cc) return
			cc.save()
			cc.setTransform(1, 0, 0, 1, 0, 0)
			cc.drawImage(img, 0, 0, c.width, c.height)
			cc.restore()
		}
		img.src = dataUrl
	}, [])

	/* Size the backing store to the device pixel ratio, then draw in CSS pixels.
	   A board is projected large; lines drawn at 1x on a retina panel look soft. */
	const sizeCanvas = useCallback(() => {
		const c = canvasRef.current
		if (!c) return
		const rect = c.getBoundingClientRect()
		if (!rect.width || !rect.height) return
		const dpr = window.devicePixelRatio || 1
		const existing = snapshot()
		c.width = Math.round(rect.width * dpr)
		c.height = Math.round(rect.height * dpr)
		const context = ctx()
		if (context) context.setTransform(dpr, 0, 0, dpr, 0, 0)
		if (existing) restore(existing)
	}, [restore, snapshot])

	useEffect(() => {
		sizeCanvas()
		window.addEventListener('resize', sizeCanvas)
		return () => window.removeEventListener('resize', sizeCanvas)
	}, [sizeCanvas])

	// Save the outgoing slide's marks and load the incoming slide's.
	useEffect(() => {
		if (lastKeyRef.current !== slideKey) {
			perSlideRef.current[lastKeyRef.current] = snapshot()
			historyRef.current = []
			restore(perSlideRef.current[slideKey] || null)
			lastKeyRef.current = slideKey
		}
	}, [slideKey, restore, snapshot])

	const pointerPos = (e) => {
		const c = canvasRef.current
		const rect = c.getBoundingClientRect()
		const point = e.touches ? e.touches[0] : e
		return { x: point.clientX - rect.left, y: point.clientY - rect.top }
	}

	const applyStroke = () => {
		const context = ctx()
		if (!context) return
		context.lineJoin = 'round'
		context.lineCap = 'round'
		if (toolRef.current === 'eraser') {
			context.globalCompositeOperation = 'destination-out'
			context.lineWidth = ERASER_WIDTH
			context.strokeStyle = 'rgba(0,0,0,1)'
		} else if (toolRef.current === 'highlighter') {
			context.globalCompositeOperation = 'source-over'
			context.lineWidth = HIGHLIGHTER_WIDTH
			context.strokeStyle = hexToRgba(colorRef.current, HIGHLIGHTER_ALPHA)
		} else {
			context.globalCompositeOperation = 'source-over'
			context.lineWidth = PEN_WIDTH
			context.strokeStyle = colorRef.current
		}
	}

	const pushHistory = () => {
		const shot = snapshot()
		if (!shot) return
		historyRef.current.push(shot)
		if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift()
	}

	const placeText = (pos) => {
		// eslint-disable-next-line no-alert
		const value = window.prompt('Text to add to the slide')
		if (!value) return
		pushHistory()
		const context = ctx()
		if (!context) return
		context.globalCompositeOperation = 'source-over'
		context.fillStyle = colorRef.current
		context.font = '600 22px Sora, sans-serif'
		context.textBaseline = 'top'
		context.fillText(value, pos.x, pos.y)
	}

	const onDown = (e) => {
		if (!active) return
		if (e.cancelable) e.preventDefault()
		const pos = pointerPos(e)
		if (toolRef.current === 'text') { placeText(pos); return }
		pushHistory()
		drawingRef.current = true
		applyStroke()
		const context = ctx()
		context.beginPath()
		context.moveTo(pos.x, pos.y)
	}

	const onMove = (e) => {
		if (!drawingRef.current) return
		if (e.cancelable) e.preventDefault()
		const pos = pointerPos(e)
		const context = ctx()
		if (!context) return
		context.lineTo(pos.x, pos.y)
		context.stroke()
	}

	const onUp = useCallback(() => { drawingRef.current = false }, [])

	useEffect(() => {
		window.addEventListener('mouseup', onUp)
		window.addEventListener('touchend', onUp)
		return () => {
			window.removeEventListener('mouseup', onUp)
			window.removeEventListener('touchend', onUp)
		}
	}, [onUp])

	const undo = () => {
		const prev = historyRef.current.pop()
		if (prev === undefined) return
		restore(prev)
	}

	const clear = () => {
		pushHistory()
		restore(null)
		perSlideRef.current[slideKey] = null
	}

	return (
		<>
			<canvas
				ref={canvasRef}
				className={`slide-annotation-canvas ${active ? 'active' : ''}`}
				onMouseDown={onDown}
				onMouseMove={onMove}
				onTouchStart={onDown}
				onTouchMove={onMove}
			/>
			{active && (
				<div className="annotation-panel" role="toolbar" aria-label="Slide markup tools">
					<div className="annotation-tools">
						{TOOLS.map(t => (
							<button
								key={t.id}
								type="button"
								className={`annotation-tool ${tool === t.id ? 'active' : ''}`}
								onClick={() => setTool(t.id)}
								aria-label={t.label}
								aria-pressed={tool === t.id}
							>{t.icon}</button>
						))}
					</div>
					<div className="annotation-colors">
						{COLORS.map(c => (
							<button
								key={c}
								type="button"
								className={`annotation-color ${color === c ? 'active' : ''}`}
								style={{ background: c }}
								onClick={() => setColor(c)}
								aria-label={`Colour ${c}`}
								aria-pressed={color === c}
							/>
						))}
					</div>
					{onToggleHighContrast && (
						<div className="annotation-hc-row">
							<button
								type="button"
								className={`annotation-hc-btn ${highContrast ? 'active' : ''}`}
								onClick={onToggleHighContrast}
								aria-pressed={!!highContrast}
							>High Contrast</button>
						</div>
					)}
					<div className="annotation-actions">
						<button type="button" className="annotation-action" onClick={undo}>Undo</button>
						<button type="button" className="annotation-action" onClick={clear}>Clear all</button>
					</div>
				</div>
			)}
		</>
	)
}
